/*
 * Project: bellcloud
 * File: apps/frontend/src/components/alarm/GlobalAlarmBorder.tsx
 * Description: 현재 알람(low/high/twa/stel)이 울리는 기기가 하나라도 있으면
 *              화면 전체에 붉은 글로우 테두리를 전역으로 표시한다.
 */

import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useDashboardLiveAggregation } from '@/api/dashboard/useDashboard';
import { useSettingsStore } from '@/store/settingsStore';
import { startAlarmLoop, stopAlarmLoop } from '@/utils/alarmSound';

/**
 * 전역 라이브 집계(liveAlarms.total)의 low/high/twa/stel `now` 중
 * 하나라도 > 0 이면 현재 알람이 울리는 기기가 있다고 판정한다.
 * 회사/사이트 필터 없이 호출하므로 사용자가 볼 수 있는 전체 기기 기준.
 */
export const useActiveAlarm = (): boolean => {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const { data } = useDashboardLiveAggregation({ enabled: isAuthenticated });

  const total = data?.liveAlarms?.total;
  if (!total) return false;

  return (
    (total.low?.now ?? 0) > 0 ||
    (total.high?.now ?? 0) > 0 ||
    (total.twa?.now ?? 0) > 0 ||
    (total.stel?.now ?? 0) > 0
  );
};

const pulse = keyframes`
  0% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.55;
  }
`;

const Overlay = styled.div<{ $glow: string }>`
  position: fixed;
  inset: 0;
  z-index: 2147483646;
  pointer-events: none;
  box-shadow: inset 0 0 80px 24px ${({ $glow }) => $glow};
  animation: ${pulse} 1.6s ease-in-out infinite;
`;

/**
 * 전역 알람 테두리 오버레이 + 경보음 루프.
 * 인증된 상태에서 알람이 울리는 기기가 있을 때만 테두리를 렌더링하고,
 * 동일 조건(+사운드 설정 ON) 동안 경보음을 반복 재생한다.
 */
export const GlobalAlarmBorder: React.FC = () => {
  const isActive = useActiveAlarm();
  const alarmSoundEnabled = useSettingsStore(state => state.alarmSoundEnabled);

  // 알람이 울리는 기기가 있고 사운드가 켜져 있으면 경보음을 반복 재생한다.
  useEffect(() => {
    if (isActive && alarmSoundEnabled) {
      startAlarmLoop();
    } else {
      stopAlarmLoop();
    }
  }, [isActive, alarmSoundEnabled]);

  // 언마운트 시 경보음 정지
  useEffect(() => stopAlarmLoop, []);

  if (!isActive) return null;

  return <Overlay $glow="rgba(231, 58, 58, 0.85)" aria-hidden="true" />;
};
